import React from 'react';
import Button from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import {
    websiteSettings,
    footer,
    header,
    features,
    openTimings as openTimingsData,
} from '@/data/content';

/** Max item labels per line in the opening-hours section */
const MOBILE_BREAKPOINT = 640;
const ITEMS_PER_LINE_DESKTOP = 5;
const ITEMS_PER_LINE_MOBILE = 3;

const AppFooter: React.FC = () => {
    const openTimings = openTimingsData || [];

    const buildOpenDays = (slots: typeof openTimings) => {
        const days = [...new Set(slots.map((s) => s.day_of_week))].sort((a, b) => a - b);
        const names = days.map((d) => footer.days[d] ?? '?');
        const lines: string[] = [];
        const itemsPerLine = window.innerWidth < MOBILE_BREAKPOINT ? ITEMS_PER_LINE_MOBILE : ITEMS_PER_LINE_DESKTOP;
        for (let i = 0; i < names.length; i += itemsPerLine) {
            lines.push(names.slice(i, i + itemsPerLine).join(', '));
        }
        return lines;
    };

    return (
        <footer
            className="bg-gray-900 text-white pt-16 pb-24 md:pb-8 border-t border-gray-800 relative z-10"
            aria-label="Footer principal"
        >
            <div className="container mx-auto px-4 max-w-7xl">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
                    {/* Brand column */}
                    <div className="flex flex-col gap-6">
                        <Link to="/" className="inline-block py-2">
                            <span className="text-2xl font-black tracking-tight text-white">
                                {websiteSettings.restaurant_name}
                                <span className="text-app-primary">.</span>
                            </span>
                        </Link>
                        <p className="text-gray-400 text-sm leading-relaxed">{footer.description}</p>
                        <div className="flex items-center gap-4 mt-2">
                            {websiteSettings.social_facebook && (
                                <a
                                    href={websiteSettings.social_facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-11 h-11 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:bg-app-primary hover:text-white hover:border-app-primary transition-all"
                                    aria-label="Facebook"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path
                                            fillRule="evenodd"
                                            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </a>
                            )}
                            {websiteSettings.social_instagram && (
                                <a
                                    href={websiteSettings.social_instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-11 h-11 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 hover:bg-app-primary hover:text-white hover:border-app-primary transition-all"
                                    aria-label="Instagram"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path
                                            fillRule="evenodd"
                                            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex flex-col gap-6">
                        <h4 className="text-lg font-bold text-white tracking-wide">{footer.quick_links_heading}</h4>
                        <ul className="flex flex-col gap-1">
                            <li>
                                <Link
                                    to="/menu"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {header.menu_label}
                                </Link>
                            </li>
                            {features.order && (
                                <li>
                                    <Link
                                        to="/menu"
                                        className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                        {header.order_label || 'Commander'}
                                    </Link>
                                </li>
                            )}
                            <li>
                                <Link
                                    to="/services"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {header.services_label}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/about-us"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {header.about_label}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {header.contact_label}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div className="flex flex-col gap-6">
                        <h4 className="text-lg font-bold text-white tracking-wide">{footer.legal_heading}</h4>
                        <ul className="flex flex-col gap-1">
                            <li>
                                <Link
                                    to="/mentions-legales"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {footer.legal_info_label}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/cgv"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {footer.cgv_label}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/politique-confidentialite"
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {footer.privacy_label}
                                </Link>
                            </li>
                            <li>
                                <Button
                                    variant="ghost"
                                    type="button"
                                    onClick={() => window.dispatchEvent(new Event('bk:reopen-consent'))}
                                    className="text-gray-400 hover:text-app-primary transition-colors inline-flex items-center gap-2 py-2 w-full text-left"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-app-primary/50"></span>{' '}
                                    {footer.cookies_label}
                                </Button>
                            </li>
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div className="flex flex-col gap-6">
                        <h4 className="text-lg font-bold text-white tracking-wide">{footer.contact_heading}</h4>
                        <div className="flex flex-col gap-4">
                            <div className="flex gap-3 items-start">
                                <div className="text-app-primary mt-0.5">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.8"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                    </svg>
                                </div>
                                <div className="text-gray-400 text-sm">
                                    <p>{websiteSettings.restaurant_address}</p>
                                    <p>{websiteSettings.restaurant_city}</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start py-1">
                                <div className="text-app-primary mt-0.5">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.8"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                    </svg>
                                </div>
                                <div className="text-gray-400 text-sm">
                                    <p>
                                        <a
                                            href={`tel:${websiteSettings.restaurant_phonenumber.replace(/[^+\d]/g, '')}`}
                                            className="hover:text-app-primary transition-colors py-2 inline-block min-h-[44px]"
                                        >
                                            {websiteSettings.restaurant_phonenumber}
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start py-1">
                                <div className="text-app-primary mt-0.5">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.8"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                </div>
                                <div className="text-gray-400 text-sm break-all">
                                    <p>
                                        <a
                                            href={`mailto:${websiteSettings.restaurant_email}`}
                                            className="hover:text-app-primary transition-colors py-2 inline-block min-h-[44px]"
                                        >
                                            {websiteSettings.restaurant_email}
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="text-app-primary mt-0.5">
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.8"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <div className="text-gray-400 text-sm">
                                    {openTimings.length === 0 ? (
                                        <p>{footer.unavailable_timings}</p>
                                    ) : (
                                        buildOpenDays(openTimings).map((line, i) => (
                                            <p key={line} className="leading-relaxed">
                                                {line}
                                            </p>
                                        ))
                                    )}
                                    <p className="text-xs text-gray-500 mt-2 italic">{footer.order_only}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-gray-500 text-sm">
                        &copy; {new Date().getFullYear()} {websiteSettings.restaurant_name}. {footer.rights_reserved}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default AppFooter;
